export const extractSvgFromText = (text: string): string | null => {
  // Match ALL SVG tags (both in code blocks and inline)
  const allSvgs: string[] = [];

  // First, try to match SVG code blocks in markdown
  const svgCodeBlockRegex = /```(?:svg|xml)?\s*\n?(<svg[\s\S]*?<\/svg>)\s*```/gi;
  let codeBlockMatch;
  while ((codeBlockMatch = svgCodeBlockRegex.exec(text)) !== null) {
    if (codeBlockMatch[1]) {
      allSvgs.push(codeBlockMatch[1].trim());
    }
  }

  // If we found SVGs in code blocks, return them all
  if (allSvgs.length > 0) {
    return allSvgs.join('\n');
  }

  // Otherwise, match ALL inline SVG tags
  const svgInlineRegex = /<svg[\s\S]*?<\/svg>/gi;
  let inlineMatch;
  while ((inlineMatch = svgInlineRegex.exec(text)) !== null) {
    allSvgs.push(inlineMatch[0].trim());
  }

  // Return all SVGs concatenated, or null if none found
  return allSvgs.length > 0 ? allSvgs.join('\n') : null;
};

export const extractAllSvgsFromConversation = (messages: Array<{ role: string; content: string }>): string[] => {
  const svgs: string[] = [];

  messages.forEach(msg => {
    if (msg.role === 'assistant') {
      const svg = extractSvgFromText(msg.content);
      if (svg) {
        svgs.push(svg);
      }
    }
  });

  return svgs;
};

export const getLatestSvg = (messages: Array<{ role: string; content: string }>): string | null => {
  // Iterate backwards to get the most recent SVG
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') {
      const svg = extractSvgFromText(messages[i].content);
      if (svg) {
        return svg;
      }
    }
  }

  return null;
};

export const extractArtifactTitlesFromMessage = (content: string): string[] => {
  const titles: string[] = [];
  const regex = /\*\*Artifact:\s*(.+?)\*\*\s*\n\s*```(?:svg|xml)?/gi;
  let match;
  while ((match = regex.exec(content)) !== null) {
    const title = match[1].trim();
    if (title.length > 0) titles.push(title);
  }
  return titles;
};

export const replaceSvgWithArtifactMessage = (text: string): string => {
  // Replace **Artifact: <title>** + SVG code blocks with a labeled artifact message
  let result = text.replace(
    /\*\*Artifact:\s*(.+?)\*\*\s*\n\s*```(?:svg|xml)?\s*\n?<svg[\s\S]*?<\/svg>\s*```/gi,
    (_match, title) => `\n\n**${title.trim()}** — Artifact rendered in canvas.\n\n`
  );

  // Replace remaining SVG code blocks without **Artifact:** prefix (old format)
  result = result.replace(/```(?:svg|xml)?\s*\n?<svg[\s\S]*?<\/svg>\s*```/gi, '\n\nArtifact rendered in canvas.\n\n');

  // Replace inline SVG tags with artifact message
  result = result.replace(/<svg[\s\S]*?<\/svg>/gi, '\n\nArtifact rendered in canvas.\n\n');

  return result;
};

export interface SvgArtifact {
  id: string;
  svgCode: string;
  timestamp: Date;
  title: string;
  messageIndex: number;
}

export const extractSvgArtifactsFromConversation = (
  messages: Array<{ role: string; content: string; timestamp?: Date }>,
  conversationTitle?: string
): SvgArtifact[] => {
  const artifacts: SvgArtifact[] = [];
  let artifactCounter = 1;

  messages.forEach((msg, index) => {
    if (msg.role === 'assistant') {
      const svg = extractSvgFromText(msg.content);
      if (svg) {
        // Try new **Artifact: <title>** format first
        const artifactTitles = extractArtifactTitlesFromMessage(msg.content);
        let title = artifactTitles.length > 0 ? artifactTitles[0] : '';

        // Fallback: extract from heading or first line (old conversations)
        if (!title) {
          const headingMatch = msg.content.match(/^#+\s*(.+)$/m);
          if (headingMatch) {
            title = headingMatch[1].trim();
          } else {
            const lines = msg.content.split('\n').filter(line => line.trim().length > 0);
            if (lines.length > 0) {
              const firstLine = lines[0];
              if (firstLine.length <= 46) {
                title = firstLine.trim();
              } else {
                // Truncate at the last space within 50 chars to avoid cutting mid-word
                const truncated = firstLine.substring(0, 50);
                const lastSpace = truncated.lastIndexOf(' ');
                title = (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated).trim() + '...';
              }
            }
          }
        }

        // Fallback to conversation title or generic name
        if (!title || title.startsWith('<')) {
          title = conversationTitle || `Artifact ${artifactCounter}`;
        }

        artifacts.push({
          id: `artifact-${index}-${Date.now()}`,
          svgCode: svg,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          title: title,
          messageIndex: index
        });
        artifactCounter++;
      }
    }
  });

  return artifacts;
};
